import React from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Mailbox, ClipboardList, CalendarCheck,
  Euro, Clock, UtensilsCrossed, Sparkles, ArrowRight, ExternalLink,
} from "lucide-react";
import { useLang } from "../LanguageContext";
import { FeesCalculators } from "../components/FeesCalculators";

const STEP_ICONS = [BookOpen, Mailbox, ClipboardList, CalendarCheck];
const PRACTICAL_ICONS = [Euro, Clock, UtensilsCrossed, Sparkles];
const FEATURE_IMG =
  "https://customer-assets.emergentagent.com/job_ecole-pleine-nature/artifacts/zt73t0bf_electronics.jpg";
const FEATURE_IMG_2 =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_ecole-pleine-nature/artifacts/snmq1vqp_guitar.webp";
const SKETCH_IMG = "/img/sortie-dessin.jpg";
const MATHS_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_ecole-pleine-nature/artifacts/wxgky6ax_maths%202.webp";

export default function Inscrire() {
  const { t } = useLang();
  const steps = t("inscrire.steps") || [];
  const cards = t("inscrire.practical_cards") || [];

  return (
    <div data-testid="page-inscrire">
      <section className="container-page pt-16 sm:pt-24 pb-12">
        <p className="eyebrow mb-4">{t("inscrire.eyebrow")}</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-ink max-w-3xl leading-tight">
          {t("inscrire.title")}
        </h1>
        <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-2xl">
          {t("inscrire.intro")}
        </p>
      </section>

      <section className="container-page pb-10">
        <div className="flex flex-wrap justify-center gap-5">
          <div className="w-full max-w-[15rem] h-64 sm:h-72 rounded-[2rem] overflow-hidden shadow-soft bg-cream-100">
            <img
              src={FEATURE_IMG}
              alt="École Pleine Nature — atelier autour d’un projet"
              className="w-full h-full object-cover block"
              loading="lazy"
            />
          </div>
          <div className="w-full max-w-[15rem] h-64 sm:h-72 rounded-[2rem] overflow-hidden shadow-soft bg-cream-100">
            <img
              src={FEATURE_IMG_2}
              alt="École Pleine Nature — pratique musicale"
              className="w-full h-full object-cover block"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="container-page py-10">
        <div className="grid lg:grid-cols-3 gap-7">
          <div className="lg:col-span-2 flex flex-col gap-7">
            <div className="card-soft p-8">
              <h2 className="font-heading text-2xl font-semibold text-ink mb-3">
                {t("inscrire.for_who_title")}
              </h2>
              <p className="text-ink-muted leading-relaxed">{t("inscrire.for_who_text")}</p>
            </div>
            <div className="card-soft p-8 bg-forest-light/40 border-forest-light">
              <h2 className="font-heading text-2xl font-semibold text-ink mb-3">
                {t("inscrire.before_title")}
              </h2>
              <p className="text-ink-muted leading-relaxed">
                {t("inscrire.before_text_a")}
                <a
                  href="https://docs.google.com/document/d/14x6_hkFcM2-UPezSFhN5qaMnDMOJ_ev2bpkK0IhN4Gs/view"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-forest underline underline-offset-4 hover:text-forest-dark font-medium"
                  data-testid="before-project-link"
                >
                  {t("inscrire.before_link")}
                </a>
                {t("inscrire.before_text_b")}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5 flex-1 min-h-[16rem]">
              <div className="relative aspect-square sm:aspect-auto overflow-hidden rounded-[2rem] shadow-soft bg-cream-100" data-testid="inscrire-photo-sketch">
                <img
                  src={SKETCH_IMG}
                  alt="École Pleine Nature — sortie dessin dans le village"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="relative aspect-square sm:aspect-auto overflow-hidden rounded-[2rem] shadow-soft bg-cream-100" data-testid="inscrire-photo-maths">
                <img
                  src={MATHS_IMG}
                  alt="École Pleine Nature — atelier maths autour de la table"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          <FeesCalculators />
        </div>
      </section>

      <section className="container-page py-12">
        <h2 className="font-heading text-3xl font-medium text-ink mb-8">
          {t("inscrire.steps_title")}
        </h2>
        <div className="grid md:grid-cols-2 gap-5">
          {steps.map((s, i) => {
            const Icon = STEP_ICONS[i] || BookOpen;
            return (
              <article
                key={s.n}
                className="card-soft p-8 flex gap-5 hover:shadow-lift hover:-translate-y-1 transition-all duration-300"
                data-testid={`step-${s.n}`}
              >
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-forest text-cream-50 grid place-items-center font-heading text-lg">
                  {s.n}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon size={18} className="text-forest" />
                    <h3 className="font-heading text-xl font-semibold text-ink">{s.title}</h3>
                  </div>
                  <p className="text-ink-muted leading-relaxed">{s.text}</p>
                  {s.link && (
                    <a
                      href={s.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-forest hover:text-forest-dark"
                      data-testid={`step-${s.n}-link`}
                    >
                      <ExternalLink size={14} /> {s.link_label}
                    </a>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="container-page py-12">
        <div className="rounded-[2rem] bg-cream-100 border border-cream-300 px-8 sm:px-12 py-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="max-w-xl">
            <p className="eyebrow mb-2">{t("inscrire.immersion_eyebrow")}</p>
            <h3 className="font-heading text-2xl font-semibold text-ink">
              {t("inscrire.immersion_title")}
            </h3>
            <p className="text-ink-muted mt-3 leading-relaxed">
              {t("inscrire.immersion_text")} <strong>{t("inscrire.immersion_price")}</strong>.
            </p>
          </div>
          <Link to="/contact?sujet=inscription" className="btn-primary shrink-0" data-testid="cta-immersion-enfant">
            {t("inscrire.immersion_cta")} <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <section className="container-page py-12">
        <h2 className="font-heading text-3xl font-medium text-ink mb-8">
          {t("inscrire.practical_title")}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((p, i) => {
            const Icon = PRACTICAL_ICONS[i] || Euro;
            return (
              <div key={i} className="card-soft p-7" data-testid={`practical-${i}`}>
                <Icon size={22} className="text-forest mb-4" />
                <h3 className="font-heading text-lg font-semibold text-ink mb-2">{p.title}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="container-page py-8">
        <div className="rounded-[2rem] bg-forest-light/50 border border-forest-light px-8 sm:px-12 py-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6" data-testid="inscrire-adult-route">
          <div className="max-w-xl">
            <h3 className="font-heading text-2xl font-semibold text-ink">
              {t("inscrire.adult_title")}
            </h3>
            <p className="text-ink-muted mt-3 leading-relaxed">{t("inscrire.adult_text")}</p>
          </div>
          <Link to="/s-engager" className="btn-outline shrink-0" data-testid="cta-adult-join">
            {t("inscrire.adult_cta")} <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <section className="container-page py-16">
        <div className="rounded-[2.5rem] bg-forest text-cream-50 px-8 sm:px-14 py-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl sm:text-3xl font-medium text-cream-50">
              {t("inscrire.final_title")}
            </h3>
            <p className="mt-3 text-cream-100/80 max-w-lg">{t("inscrire.final_text")}</p>
          </div>
          <Link
            to="/contact?sujet=inscription"
            className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-cream-50 text-forest-deep font-medium hover:bg-cream-100 transition-colors"
            data-testid="cta-contact-admission"
          >
            {t("inscrire.final_cta")} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
